import type { ZodSimplify } from '@mntn-dev/utility-types'
import { z } from 'zod'
import { CreditProgramKindSchema } from '../billing/credit-program.ts'
import { CurrencySchema } from '../billing/currency.ts'

const BaseCurrencySchema = z.enum(['DB']) // the unit used to store values in the database in US Cents

export const ExchangeUnitSchema = z.union([
  CreditProgramKindSchema,
  CurrencySchema,
  BaseCurrencySchema,
])

//  "DB" | "mntn_credits" | "meta" | "tiktok" | "linkedin" | "USD"

export type ExchangeUnit = z.infer<typeof ExchangeUnitSchema>

export const ExchangeRateSchema = z.record(ExchangeUnitSchema, z.number())

export type ExchangeRate = z.infer<typeof ExchangeRateSchema>

export type ExchangeRateMap = Record<ExchangeUnit, number>
export const ExchangeRateMapSchema = z.custom<ExchangeRateMap>((data) => {
  return z.record(ExchangeUnitSchema, z.number()).safeParse(data).success
})

export const CentSchema = z.number().int().brand<'Cent'>()
export type Cent = ZodSimplify<typeof CentSchema>
export const Cent = (value: number): Cent => CentSchema.parse(value)

export const DollarSchema = z.number().brand<'Dollar'>()
export type Dollar = ZodSimplify<typeof DollarSchema>
export const Dollar = (value: number): Dollar => DollarSchema.parse(value)

export const MntnCreditSchema = z.number().brand<'MntnCredit'>()
export type MntnCredit = ZodSimplify<typeof MntnCreditSchema>
export const MntnCredit = (value: number): MntnCredit =>
  MntnCreditSchema.parse(value)

export const MetaCreditSchema = z.number().brand<'MetaCredit'>()
export type MetaCredit = ZodSimplify<typeof MetaCreditSchema>
export const MetaCredit = (value: number): MetaCredit =>
  MetaCreditSchema.parse(value)

export const TiktokCreditSchema = z.number().brand<'TiktokCredit'>()
export type TiktokCredit = ZodSimplify<typeof TiktokCreditSchema>
export const TiktokCredit = (value: number): TiktokCredit =>
  TiktokCreditSchema.parse(value)

export const LinkedinCreditSchema = z.number().brand<'LinkedinCredit'>()
export type LinkedinCredit = ZodSimplify<typeof LinkedinCreditSchema>
export const LinkedinCredit = (value: number): LinkedinCredit =>
  LinkedinCreditSchema.parse(value)

export const CreditSchema = z.union([
  MntnCreditSchema,
  MetaCreditSchema,
  TiktokCreditSchema,
  LinkedinCreditSchema,
])
export type Credit = ZodSimplify<typeof CreditSchema>

export const ExchangeUnitSchemaMap: Record<ExchangeUnit, z.ZodType<number>> = {
  DB: CentSchema,
  mntn_credits: MntnCreditSchema,
  meta: MetaCreditSchema,
  tiktok: TiktokCreditSchema,
  linkedin: LinkedinCreditSchema,
  USD: DollarSchema,
}

export const ExchangeValueSchema = z.union([
  CentSchema,
  MntnCreditSchema,
  MetaCreditSchema,
  TiktokCreditSchema,
  LinkedinCreditSchema,
  DollarSchema,
])

export type ExchangeValue = z.infer<typeof ExchangeValueSchema>

export type ToExchangeValue<T extends ExchangeUnit> = T extends 'DB'
  ? Cent
  : T extends 'USD'
    ? Dollar
    : T extends 'mntn_credits'
      ? MntnCredit
      : T extends 'meta'
        ? MetaCredit
        : T extends 'tiktok'
          ? TiktokCredit
          : T extends 'linkedin'
            ? LinkedinCredit
            : never
